/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/Combobox.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronsUpDown, Search, Check } from 'lucide-react';
import { I } from './ui';

/**
 * Searchable combobox — the only picker in Kontor (no native <select>).
 * Type-to-filter, autofocused search, circular arrows, PageUp/Down, Enter selects,
 * Esc restores focus, Tab moves on, Cmd/Ctrl+K and Cmd/Ctrl+Enter pass through. Keys typed while it opens are
 * buffered into the search, so fast typing right after opening loses nothing.
 *
 * No automated test: vitest runs only server/test with no DOM environment (no jsdom, happy-dom or Testing Library).
 * The type-ahead buffer was checked by hand with real key presses (docs/work/q6-ux-tests.md, «Combobox»).
 */
export interface Option { value: string; label: string; secondary?: string; keywords?: string }

/** The keyboard event fields the key handler reads: a React event from the search field or a native one from the buffer. */
type KeyLike = Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'preventDefault' | 'stopPropagation'>;

const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

function focusSibling(from: HTMLElement, backward: boolean) {
  const all = [...document.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null && !el.closest('.popover'));
  const i = all.indexOf(from);
  (all[i + (backward ? -1 : 1)] ?? from).focus();
}

export function Combobox({ value, options, onChange, placeholder = 'Auswählen…', searchPlaceholder = 'Suchen…', className = '', renderValue, ariaLabel, width, disabled }: {
  value: string | null; options: Option[]; onChange: (v: string) => void; placeholder?: string; searchPlaceholder?: string;
  className?: string; renderValue?: (o: Option | undefined) => ReactNode; ariaLabel?: string; width?: string; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; width: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const tokens = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return options;
    return options.filter((o) => { const hay = `${o.label} ${o.secondary ?? ''} ${o.keywords ?? ''} ${o.value}`.toLowerCase(); return tokens.every((t) => hay.includes(t)); });
  }, [q, options]);

  const place = () => {
    const r = trigger.current!.getBoundingClientRect();
    const w = Math.max(r.width, 256);
    const left = Math.min(r.left, window.innerWidth - w - 16);
    const below = window.innerHeight - r.bottom;
    setPos(below < 320 && r.top > below ? { left, bottom: window.innerHeight - r.top + 6, width: w } : { left, top: r.bottom + 6, width: w });
  };

  // Mirrors `open` without waiting for a render: a key that reaches the trigger while the list is already opening must
  // extend the query, not reopen the list with an empty one.
  const isOpen = useRef(false);
  const openIt = (typed = '') => {
    if (disabled) return;
    if (isOpen.current) { if (typed) setQ((s) => s + typed); return; }
    isOpen.current = true;
    place(); setQ(typed); setActive(Math.max(0, options.findIndex((o) => o.value === value))); setOpen(true);
  };
  const close = (refocus = true) => { isOpen.current = false; setOpen(false); if (refocus) trigger.current?.focus({ preventScroll: true }); };

  useLayoutEffect(() => { if (open) { const f = requestAnimationFrame(() => input.current?.focus({ preventScroll: true })); return () => cancelAnimationFrame(f); } }, [open]);
  // Type-ahead buffer: until the search field has focus, keys land on the trigger (or, where a click does not focus a
  // button, on the page). While the list is open they are taken in the capture phase and go into the query, so nothing
  // typed right after opening is lost; navigation keys act as in the field.
  const keyHandler = useRef<(e: KeyLike) => void>(() => {});
  useLayoutEffect(() => {
    if (!open) return;
    const early = (e: KeyboardEvent) => (e.target === trigger.current || e.target === document.body || e.target === document.documentElement) && !e.isComposing;
    const onDown = (e: KeyboardEvent) => {
      if (!early(e)) return;
      const printable = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (printable || e.key === 'Backspace') {
        e.preventDefault(); e.stopPropagation();
        setQ((s) => (printable ? s + e.key : s.slice(0, -1)));
        input.current?.focus({ preventScroll: true });
      } else {
        keyHandler.current(e);
        if (e.defaultPrevented) e.stopPropagation();
      }
    };
    // A space typed on the trigger would otherwise press it on key-up and close the list.
    const onUp = (e: KeyboardEvent) => { if (early(e) && e.key === ' ') e.preventDefault(); };
    document.addEventListener('keydown', onDown, true);
    document.addEventListener('keyup', onUp, true);
    return () => { document.removeEventListener('keydown', onDown, true); document.removeEventListener('keyup', onUp, true); };
  }, [open]);
  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (!list.current?.parentElement?.contains(t) && !trigger.current?.contains(t)) close(false); };
    const onScroll = (e: Event) => { if (!list.current?.contains(e.target as Node)) place(); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('resize', place); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);
  useEffect(() => { list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [active, open]);

  const pick = (o: Option | undefined) => { if (!o) return; onChange(o.value); close(); };

  const onKey = (e: KeyLike) => {
    const n = filtered.length;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && (e.key === 'k' || e.key === 'K')) { close(false); return; } // global palette takes over
    if (e.key === 'Enter' && mod) { close(); return; }                    // let forms submit
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setActive((a) => (n ? (a + 1) % n : 0)); break;
      case 'ArrowUp': e.preventDefault(); setActive((a) => (n ? (a - 1 + n) % n : 0)); break;
      case 'PageDown': e.preventDefault(); setActive((a) => Math.min(n - 1, a + 8)); break;
      case 'PageUp': e.preventDefault(); setActive((a) => Math.max(0, a - 8)); break;
      case 'Home': if (!q) { e.preventDefault(); setActive(0); } break;
      case 'End': if (!q) { e.preventDefault(); setActive(n - 1); } break;
      case 'Enter': e.preventDefault(); pick(filtered[active]); break;
      case 'Escape': e.preventDefault(); e.stopPropagation(); close(); break;
      case 'Tab': e.preventDefault(); isOpen.current = false; setOpen(false); focusSibling(trigger.current!, e.shiftKey); break;
    }
  };
  keyHandler.current = onKey;

  return (
    <>
      <button
        ref={trigger} type="button" className={`btn combo-trigger ${className}`} style={width ? { width } : undefined} data-combo-trigger
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} disabled={disabled}
        onClick={() => (isOpen.current ? close() : openIt())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openIt(); }
          else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && e.key !== ' ') { openIt(e.key); e.preventDefault(); }
        }}
      >
        <span className="val">{renderValue ? renderValue(selected) : selected ? selected.label : <span className="dim">{placeholder}</span>}</span>
        <I icon={ChevronsUpDown} sm className="dim" />
      </button>
      {open && pos && createPortal(
        <div className="popover" style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}>
          <div className="combo-search">
            <I icon={Search} sm />
            <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder={searchPlaceholder}
              role="combobox" aria-expanded="true" aria-controls="combo-list" aria-activedescendant={filtered[active] ? `opt-${filtered[active].value}` : undefined} aria-autocomplete="list" />
          </div>
          <div className="combo-list" role="listbox" id="combo-list" ref={list}>
            {filtered.length === 0 && <div className="combo-empty">Keine Treffer für «{q}»</div>}
            {filtered.map((o, i) => (
              <div key={o.value} id={`opt-${o.value}`} role="option" aria-selected={i === active} className="combo-opt"
                onMouseMove={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(o); }}>
                <span className="primary">{o.label}</span>
                {o.secondary && <span className="secondary">{o.secondary}</span>}
                {o.value === value && <I icon={Check} sm className="check" />}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
